var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IconButton, Switch, Stack, Typography, Tooltip } from "@mui/material";
/**
 * Converts camelCase or PascalCase keys into human-readable labels.
 *
 * @param key - The camelCase or PascalCase string to format
 * @returns A human-readable label with proper spacing and capitalization
 */
function formatKeyAsLabel(key) {
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
}
/**
 * Builds the final responsive table structure from pre-mapped row data.
 *
 * This function:
 * 1. Discovers all column keys from the mapped rows
 * 2. Filters out excluded columns
 * 3. Applies column metadata (labels, alignment, formatting, etc.)
 * 4. Sorts columns by their specified order
 * 5. Returns the complete table data structure
 *
 * @param rows - Array of row objects with transformed data
 * @param columnConfig - Configuration metadata for each column
 * @param excludeColumns - Array of column keys to exclude from the table
 * @returns Complete table data with columns and rows ready for rendering
 *
 * @internal This is an internal helper function used by buildResponsiveTableData
 */
function buildResponsiveTableFromMappedRows(rows, columnConfig, excludeColumns = []) {
    const allColumnKeys = new Set();
    rows.forEach((row) => {
        Object.keys(row).forEach((key) => allColumnKeys.add(key));
    });
    // Filter columns based on options
    let columnKeys = Array.from(allColumnKeys);
    if (excludeColumns.length > 0) {
        columnKeys = columnKeys.filter((key) => !excludeColumns.includes(key));
    }
    // Build column definitions from metadata
    const columns = columnKeys
        .map((key) => {
        const config = columnConfig[key];
        return {
            id: key,
            label: (config === null || config === void 0 ? void 0 : config.label) || formatKeyAsLabel(key),
            minWidth: (config === null || config === void 0 ? void 0 : config.minWidth) || 100,
            align: (config === null || config === void 0 ? void 0 : config.align) || "left",
            format: config === null || config === void 0 ? void 0 : config.format,
            order: (config === null || config === void 0 ? void 0 : config.order) || 999,
        };
    })
        .sort((a, b) => (a.order || 999) - (b.order || 999))
        .map((_a) => {
        var { order } = _a, col = __rest(_a, ["order"]);
        return col;
    });
    // Filter row data to include only the final columns
    const filteredRows = rows.map((row) => {
        const filteredRow = {};
        columnKeys.forEach((key) => {
            if (row.hasOwnProperty(key)) {
                filteredRow[key] = row[key];
            }
        });
        return filteredRow;
    });
    return {
        columns,
        rows: filteredRows,
    };
}
/**
 * Transforms raw data into a pure table data structure using component descriptors.
 *
 * This is a **pure function** that:
 * - Takes raw data and transforms it using a provided mapper function
 * - Returns component descriptors instead of JSX (for better testability)
 * - Provides helper functions for creating UI components as data structures
 * - Maintains functional purity with no side effects
 * - Uses string-based icon names instead of JSX for better separation of concerns
 *
 * @template T - The type of the raw data items
 *
 * @param options - Configuration object containing:
 * @param options.data - Array of raw data items to transform
 * @param options.columnConfig - Metadata for table columns (labels, alignment, etc.)
 * @param options.rowMapper - Function that transforms each data item into a table row
 * @param options.excludeColumns - Optional array of column keys to exclude
 *
 * @returns Pure table data structure with:
 * - `columns`: Array of column definitions with metadata
 * - `rows`: Array of row objects containing component descriptors (not JSX)
 *
 * @example
 * ```tsx
 * const tableData = buildResponsiveTableData({
 *   data: products,
 *   columnConfig: PRODUCT_COLUMNS,
 *   rowMapper: (product, helpers) => ({
 *     name: product.name,
 *     price: helpers.formatCurrency(product.price),
 *     actions: helpers.createIconButton("edit", () => edit(product.id), "Edit product"),
 *   }),
 * });
 * ```
 *
 * @see {@link renderResponsiveTableComponents} - Use this to convert descriptors to JSX
 * @see {@link RowMapperHelpers} - Available helper functions for creating components
 */
export const buildResponsiveTableData = (options) => {
    const { data, columnConfig, rowMapper, excludeColumns = [] } = options;
    // Create pure helper functions (no JSX)
    const helpers = {
        columnConfig,
        formatCurrency: (amount) => `$${amount.toFixed(2)}`,
        formatDate: (date) => new Date(date).toLocaleDateString(),
        formatPercent: (value) => `${value}%`,
        // These return action descriptors, not JSX
        createIconButton: (icon, onClick, ariaLabel, color = "default", tooltip) => ({
            type: "iconButton",
            icon,
            onClick,
            ariaLabel,
            color,
            tooltip,
        }),
        createSwitch: (checked, onChange, disabled = false, ariaLabel, tooltip) => ({
            type: "switch",
            checked,
            onChange,
            disabled,
            ariaLabel,
            tooltip,
        }),
        createImage: (src, alt, width, height) => ({
            type: "image",
            src,
            alt,
            width,
            height,
        }),
        createStack: (direction, spacing, children, options = {}) => ({
            type: "stack",
            direction,
            spacing,
            children,
            alignItems: options.alignItems,
            justifyContent: options.justifyContent,
        }),
        createConditional: (condition, component) => ({
            type: "conditional",
            condition,
            component,
        }),
        createCustomComponent: (componentName, props, tooltip) => ({
            type: "customComponent",
            componentName,
            props,
            tooltip,
        }),
    };
    // Map data using helpers
    const rows = data.map((item) => rowMapper(item, helpers));
    // Build columns from the mapped rows
    return buildResponsiveTableFromMappedRows(rows, columnConfig, excludeColumns);
};
/**
 * Converts pure table data with component descriptors into JSX components.
 *
 * This function:
 * - Takes the output from `buildResponsiveTableData`
 * - Converts component descriptors into actual React JSX elements
 * - Handles custom components through a component registry
 * - Resolves string-based icon names through an icon registry
 * - Applies universal tooltip support to interactive components
 * - Maintains the same table structure but with rendered components
 *
 * @param tableData - Pure table data structure from `buildResponsiveTableData`
 * @param options - Optional rendering configuration
 * @param options.componentRegistry - Map of component names to React components for custom components
 * @param options.iconRegistry - Map of icon names to React icon components for string-based icons
 *
 * @returns Table data with JSX components instead of descriptors
 *
 * @example
 * ```tsx
 * const { columns, rows } = renderResponsiveTableComponents(tableData, {
 *   componentRegistry: {
 *     AuthEditButton: AuthEditButtonComponent,
 *   },
 *   iconRegistry: {
 *     edit: EditIcon,
 *     delete: DeleteIcon,
 *     moreHoriz: MoreHorizIcon,
 *   },
 * });
 * ```
 *
 * @see {@link buildResponsiveTableData} - Use this first to create pure table data
 * @see {@link ComponentRegistry} - Type definition for custom component registry
 * @see {@link IconRegistry} - Type definition for icon registry
 */
export const renderResponsiveTableComponents = (tableData, options = {}) => {
    const { componentRegistry = {}, iconRegistry = {} } = options;
    /**
     * Helper function to conditionally wrap a component with a Tooltip.
     *
     * This function provides universal tooltip support for any interactive component
     * descriptor (IconButton, Switch, CustomComponent). The tooltip is only applied
     * when the tooltip configuration is provided and not disabled.
     *
     * @param component - The React component to potentially wrap
     * @param tooltip - Optional tooltip configuration with title, placement, and disabled flag
     * @returns The component, optionally wrapped with a MUI Tooltip
     *
     * @example
     * ```tsx
     * // Component without tooltip
     * wrapWithTooltip(<IconButton>...</IconButton>, undefined);
     *
     * // Component with tooltip
     * wrapWithTooltip(<IconButton>...</IconButton>, {
     *   title: "Click to edit",
     *   placement: "top",
     *   disabled: false
     * });
     * ```
     */
    const wrapWithTooltip = (component, tooltip) => {
        if (tooltip && !tooltip.disabled) {
            return (_jsx(Tooltip, Object.assign({ title: tooltip.title, placement: tooltip.placement || "top" }, { children: _jsx("span", { children: component }) })));
        }
        return component;
    };
    /**
     * Recursively renders component descriptors into JSX elements.
     *
     * This function handles all component descriptor types:
     * - `iconButton`: Creates MUI IconButton with icon registry resolution and tooltip support
     * - `switch`: Creates MUI Switch with tooltip support
     * - `image`: Creates HTML img element
     * - `stack`: Creates MUI Stack layout with recursive child rendering
     * - `conditional`: Renders component only if condition is true
     * - `customComponent`: Uses component registry to render custom components with tooltip support
     *
     * Icon Resolution:
     * - String icons are resolved through the icon registry
     * - JSX icons are used directly (backward compatibility)
     * - Unknown string icons fall back to text display
     *
     * Universal Tooltips:
     * - IconButton, Switch, and CustomComponent descriptors support tooltips
     * - Tooltips are conditionally applied based on configuration
     * - Uses MUI Tooltip component with configurable placement
     *
     * @param descriptor - Component descriptor object with type and props
     * @returns React JSX element or null
     *
     * @internal This function is used internally by renderResponsiveTableComponents
     */
    const renderComponent = (descriptor) => {
        switch (descriptor.type) {
            case "iconButton":
                // Resolve icon - use icon registry for string names, fallback to JSX
                let iconElement;
                if (typeof descriptor.icon === "string") {
                    const IconComponent = iconRegistry[descriptor.icon];
                    iconElement = IconComponent ? _jsx(IconComponent, {}) : _jsx("span", { children: descriptor.icon });
                }
                else {
                    iconElement = descriptor.icon;
                }
                const iconButton = (_jsx(IconButton, Object.assign({ color: descriptor.color, onClick: descriptor.onClick, "aria-label": descriptor.ariaLabel }, { children: iconElement })));
                return wrapWithTooltip(iconButton, descriptor.tooltip);
            case "switch":
                const switchComponent = (_jsx(Switch, { checked: descriptor.checked, onChange: descriptor.onChange, disabled: descriptor.disabled, inputProps: { "aria-label": descriptor.ariaLabel } }));
                return wrapWithTooltip(switchComponent, descriptor.tooltip);
            case "image":
                return _jsx("img", { src: descriptor.src, alt: descriptor.alt, width: descriptor.width, height: descriptor.height });
            case "stack":
                return (_jsx(Stack, Object.assign({ direction: descriptor.direction, spacing: descriptor.spacing, alignItems: descriptor.alignItems, justifyContent: descriptor.justifyContent }, { children: descriptor.children.map((child, index) => (_jsx("div", { children: renderComponent(child) }, index))) })));
            case "conditional":
                return descriptor.condition && descriptor.component ? renderComponent(descriptor.component) : null;
            case "customComponent":
                // Use component registry to render custom components
                const CustomComponent = componentRegistry[descriptor.componentName];
                if (CustomComponent) {
                    const customComponent = _jsx(CustomComponent, Object.assign({}, descriptor.props));
                    return wrapWithTooltip(customComponent, descriptor.tooltip);
                }
                // Fallback for unknown components
                const fallbackComponent = (_jsxs(Typography, Object.assign({ variant: "body2" }, { children: ["Unknown Component: ", descriptor.componentName] })));
                return wrapWithTooltip(fallbackComponent, descriptor.tooltip);
            default:
                return null;
        }
    };
    const processedRows = tableData.rows.map((row) => {
        const processedRow = {};
        Object.entries(row).forEach(([key, value]) => {
            if (value && typeof value === "object" && "type" in value) {
                // Convert component descriptors to JSX
                processedRow[key] = renderComponent(value);
            }
            else {
                processedRow[key] = value;
            }
        });
        return processedRow;
    });
    return Object.assign(Object.assign({}, tableData), { rows: processedRows });
};
